import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Modal, 
  Avatar, 
  Tabs, 
  Tab, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  IconButton, 
  Tooltip, 
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Snackbar,
  Alert,
  Slide
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { supabase } from '../supabaseClient';
import { generateRandomName } from '../utils/nameGenerator';

const EditProfile = ({ open, onClose, user, onUpdate }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [userLogos, setUserLogos] = useState([]);
  const [logoLoading, setLogoLoading] = useState(false);
  const [newLogo, setNewLogo] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [editingLogoId, setEditingLogoId] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) {
      fetchProfile(user.id);
      setEmail(user.email || '');
      fetchUserLogos(user.id);
    }
  }, [user]);
  
  // Listen for tab selection event
  useEffect(() => {
    const handleSetProfileTab = (event) => {
      if (event.detail && typeof event.detail.tabIndex !== 'undefined') {
        setTabValue(event.detail.tabIndex);
      }
    };
    
    window.addEventListener('setProfileTab', handleSetProfileTab);
    
    return () => {
      window.removeEventListener('setProfileTab', handleSetProfileTab);
    };
  }, []);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('name, avatar_url')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Profil yüklenirken hata:', error);
    } else {
      if (data && data.name) {
        setName(data.name);
      } else {
        const randomName = generateRandomName();
        setName(randomName);
        
        const { error: updateError } = await supabase
          .from('profiles')
          .upsert({ id: userId, name: randomName }, { onConflict: 'id' });
        
        if (updateError) {
          console.error('Rastgele isim güncellenirken hata:', updateError);
        }
      }
      setAvatarUrl(data?.avatar_url || '');
    }
  };

  const fetchUserLogos = async (userId) => {
    try {
      setLogoLoading(true);
      const { data, error } = await supabase
        .from('user_logos')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserLogos(data || []);
    } catch (err) {
      console.error('Logolar yüklenirken hata:', err);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        alert('Dosya çok büyük. Lütfen 50MB altında bir resim seçin.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Lütfen bir resim dosyası seçin.');
        return;
      }
      setAvatar(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatar) return null;

    const fileExt = avatar.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatar, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      return publicUrl;
    } catch (error) {
      if (error.message.includes('Payload too large')) {
        alert('Resim dosyası çok büyük. Lütfen daha küçük bir resim deneyin.');
      } else if (error.message.includes('new row violates row-level security policy')) {
        alert('İzin hatası. Lütfen giriş yaptığınızdan emin olun ve tekrar deneyin.');
      } else {
        alert(`Profil resmi yüklenirken hata: ${error.message}`);
      }
      return null;
    }
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit for logos
        alert('Dosya çok büyük. Lütfen 5MB altında bir logo seçin.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Lütfen bir resim dosyası seçin.');
        return;
      }
      setNewLogo(file);
      setLogoDialogOpen(true);
    }
  };

  const uploadLogo = async () => {
    if (!newLogo) return null;

    const fileExt = newLogo.name.split('.').pop();
    const fileName = `logo_${Date.now()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from('user-logos')
        .upload(filePath, newLogo, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('user-logos')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      return { url: publicUrl, path: filePath };
    } catch (error) {
      console.error('Logo yüklenirken hata:', error);
      alert(`Logo yüklenirken hata: ${error.message}`);
      return null;
    }
  };

  const handleSaveLogo = async () => {
    try {
      setLogoLoading(true);
      
      // If editing existing logo, just update the name
      if (editingLogoId) {
        const { error } = await supabase
          .from('user_logos')
          .update({ 
            logo_name: logoName,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingLogoId);
          
        if (error) throw error;
        
        setLogoDialogOpen(false);
        setEditingLogoId(null);
        setLogoName('');
        fetchUserLogos(user.id);
        return;
      }
      
      // Otherwise upload new logo
      const logoData = await uploadLogo();
      if (!logoData) return;

      const { error } = await supabase
        .from('user_logos')
        .insert({
          user_id: user.id,
          logo_name: logoName || `Logo ${new Date().toLocaleDateString()}`,
          logo_url: logoData.url,
          storage_path: logoData.path,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setLogoDialogOpen(false);
      setNewLogo(null);
      setLogoName('');
      fetchUserLogos(user.id);
    } catch (error) {
      console.error('Logo kaydedilirken hata:', error);
      alert(`Logo kaydedilirken hata: ${error.message}`);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleDeleteLogo = async (logoId, storagePath) => {
    if (!window.confirm('Bu logoyu silmek istediğinizden emin misiniz?')) return;
    
    try {
      setLogoLoading(true);
      
      // Delete from database
      const { error: dbError } = await supabase
        .from('user_logos')
        .delete()
        .eq('id', logoId);
        
      if (dbError) throw dbError;
      
      // Delete from storage
      if (storagePath) {
        const { error: storageError } = await supabase.storage
          .from('user-logos')
          .remove([storagePath]);
          
        if (storageError) {
          console.error('Storage dosyası silinirken hata:', storageError);
          // Continue anyway as the DB record is already deleted
        }
      }
      
      // Refresh the list
      fetchUserLogos(user.id);
    } catch (error) {
      console.error('Logo silinirken hata:', error);
      alert(`Logo silinirken hata: ${error.message}`);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleEditLogo = (logo) => {
    setEditingLogoId(logo.id);
    setLogoName(logo.logo_name || '');
    setLogoDialogOpen(true);
  };
  
  // Function to add a notification to the queue
  const addNotification = (message, severity = 'success', autoHideDuration = 4000) => {
    const id = Date.now(); // Unique ID for each notification
    setNotifications(prev => [...prev, {
      id,
      message,
      severity,
      autoHideDuration,
      open: true
    }]);
    
    // Auto-remove notification after duration
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, autoHideDuration + 500); // Add a small buffer for animation
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Easter egg: Check if the name contains certain keywords
      const easterEggWords = ['admin', 'süper mimar', 'baş mimar', 'usta mimar', 'mimar bey'];
      const hasEasterEgg = easterEggWords.some(word => 
        name.toLowerCase().includes(word.toLowerCase())
      );
      
      if (hasEasterEgg) {
        // Show a fun message if they try to use a special name
        const messages = [
          'Bu kadar iddialı bir isim! 😄',
          'Mimarlık dünyasının yeni yıldızı! ⭐',
          'Admin olmak için biraz erken değil mi? 😉',
          'Vay! Büyük usta aramıza hoş geldin! 🏛️',
          'Tarihe adınızı yazdırmak için doğru yerdesiniz! 📜'
        ];
        const randomMessage = messages[Math.floor(Math.random() * messages.length)];
        addNotification(randomMessage, 'info');
      }
      
      // Easter egg: If they change their name more than 3 times in a session
      const nameChangeCount = parseInt(localStorage.getItem('nameChangeCount') || '0');
      localStorage.setItem('nameChangeCount', (nameChangeCount + 1).toString());
      
      if (nameChangeCount >= 3) {
        const indecisiveMessages = [
          'Karar veremiyorsunuz galiba? Mimar olarak bu kadar kararsız olmamalısınız! 😄',
          'İsim değiştirme konusunda bir dünya rekoruna doğru ilerliyorsunuz! 🏆',
          'Bu kadar çok isim değiştirirseniz, arkadaşlarınız sizi nasıl bulacak? 🔍',
          'Kimlik bunalımı mı yaşıyorsunuz? Bir mimar psikologumuz var! 🧠'
        ];
        setTimeout(() => {
          addNotification(indecisiveMessages[Math.floor(Math.random() * indecisiveMessages.length)], 'warning', 5000);
        }, 1000);
      }

      let newAvatarUrl = avatarUrl;
      if (avatar) {
        newAvatarUrl = await uploadAvatar();
        if (!newAvatarUrl) return;
      }

      let updateData = {
        data: { name: name, avatar_url: newAvatarUrl }
      };

      if (email !== user.email) {
        updateData.email = email;
      }

      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email: email });
        if (emailError) throw emailError;
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .upsert({ 
          id: user.id,
          username: name.toLowerCase().replace(/\s+/g, '_'),
          name: name, 
          avatar_url: newAvatarUrl,
          updated_at: new Date()
        })
        .select()
        .single();

      if (profileError) throw profileError;

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      if (showPasswordFields && password) {
        if (password !== confirmPassword) {
          throw new Error("Şifreler eşleşmiyor");
        }
        
        // Easter egg: Check for funny passwords
        const funnyPasswords = ['123456', 'password', 'şifre', '111111', 'qwerty', 'mimar'];
        if (funnyPasswords.includes(password.toLowerCase())) {
          const funnyMessages = [
            'Gerçekten mi? Bu şifreyi bir ilkokul öğrencisi bile tahmin edebilir! 🙈',
            'Bu şifre o kadar güvenli ki, komşunuzun kedisi bile hesabınıza girebilir! 🐱',
            'Şifreniz, "Lütfen beni hackleyin" diye bağırıyor! 📢',
            'Bir mimar olarak tasarımlarınız umarım şifrenizden daha yaratıcıdır! 🏗️'
          ];
          addNotification(funnyMessages[Math.floor(Math.random() * funnyMessages.length)], 'warning', 5000);
          // Continue anyway, it's just a joke
        }
        
        const { data: passwordData, error: passwordError } = await supabase.auth.updateUser({
          password: password
        });
        if (passwordError) {
          throw new Error('Şifre güncellenirken hata: ' + passwordError.message);
        }
        if (!passwordData.user) {
          throw new Error('Şifre güncellemesi başarısız oldu');
        }
        setPassword('');
        setConfirmPassword('');
        setShowPasswordFields(false);
      }

      onUpdate(session, profileData);

      setAvatarUrl(newAvatarUrl);
      setPassword('');
      setConfirmPassword('');
      setShowPasswordFields(false);

      // Easter egg: Random success messages
      const successMessages = [
        'Profil başarıyla güncellendi! Artık daha havalı görünüyorsunuz! ✨',
        'Güncelleme başarılı! Mimarlık dünyası sizinle gurur duyuyor! 🏛️',
        'Profil güncellendi! Şimdi bir kahve molası hak ettiniz! ☕',
        'Harika! Profiliniz artık bir sanat eseri gibi! 🎨',
        'Güncelleme tamamlandı! Şimdi dünyanın en şık mimarı sizsiniz! 👔'
      ];
      
      addNotification(successMessages[Math.floor(Math.random() * successMessages.length)], 'success');
      // Don't close the dialog after saving changes
    } catch (error) {
      addNotification(`Profil güncellenirken hata: ${error.message || 'Bilinmeyen bir hata oluştu'}`, 'error', 6000);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '600px' },
        maxWidth: '700px',
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: '12px',
        boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
        p: { xs: 2, sm: 3 },
        border: theme => `1px solid ${theme.palette.divider}`,
        background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        backdropFilter: 'blur(8px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: theme => `
            radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 50%),
            repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px),
            repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px)
          `,
          backgroundSize: '100% 100%, 20px 20px, 20px 20px',
          animation: 'gradientMove 20s ease-in-out infinite',
          zIndex: -1,
          borderRadius: 'inherit',
          opacity: 0.5,
        }
      }}>
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom
          sx={{
            color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
            fontWeight: 600,
            textAlign: 'center',
            mb: 2
          }}
        >
          Hesap Ayarları
        </Typography>
        
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Profil" />
          <Tab label="Logolar" />
        </Tabs>
        
        {tabValue === 0 && (
          <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Avatar
              src={avatar ? URL.createObjectURL(avatar) : avatarUrl}
              sx={{
                width: 100,
                height: 100,
                border: theme => `2px solid ${theme.palette.primary.main}40`,
                background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: theme => `0 4px 20px ${theme.palette.primary.main}40`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05) rotate(5deg)',
                  boxShadow: theme => `0 8px 25px ${theme.palette.primary.main}60`,
                }
              }}
            />
          </Box>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              mb: 3,
              py: 1.5,
              borderRadius: '8px',
              fontWeight: 600,
              borderColor: theme => theme.palette.primary.main,
              color: theme => theme.palette.primary.main,
              '&:hover': {
                borderColor: theme => theme.palette.primary.dark,
                backgroundColor: theme => `${theme.palette.primary.main}10`,
              },
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              // Easter egg: 10% chance to show a funny message on hover
              if (Math.random() < 0.1) {
                const funnyTooltip = document.createElement('div');
                funnyTooltip.innerText = ['Selfie zamanı! 📸', 'Yakışıklı/Güzel görünüyorsunuz! 😎', 'En iyi açınızı seçin! 📐', 'Gülümseyin! 😁'][Math.floor(Math.random() * 4)];
                funnyTooltip.style.position = 'absolute';
                funnyTooltip.style.top = '-30px';
                funnyTooltip.style.left = '50%';
                funnyTooltip.style.transform = 'translateX(-50%)';
                funnyTooltip.style.backgroundColor = '#333';
                funnyTooltip.style.color = '#fff';
                funnyTooltip.style.padding = '5px 10px';
                funnyTooltip.style.borderRadius = '5px';
                funnyTooltip.style.fontSize = '12px';
                funnyTooltip.style.zIndex = '1000';
                funnyTooltip.style.opacity = '0';
                funnyTooltip.style.transition = 'opacity 0.3s, top 0.3s';
                
                e.currentTarget.appendChild(funnyTooltip);
                
                setTimeout(() => {
                  funnyTooltip.style.opacity = '1';
                  funnyTooltip.style.top = '-40px';
                }, 10);
                
                setTimeout(() => {
                  funnyTooltip.style.opacity = '0';
                  funnyTooltip.style.top = '-30px';
                  setTimeout(() => {
                    e.currentTarget.removeChild(funnyTooltip);
                  }, 300);
                }, 2000);
              }
            }}
          >
            Profil Resmi Değiştir
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                handleFileChange(e);
                
                // Easter egg: 5% chance after selecting a file
                if (Math.random() < 0.05) {
                  setTimeout(() => {
                    const messages = [
                      'Bu fotoğrafta çok profesyonel görünüyorsunuz! 👔',
                      'Vay! Bu fotoğraf LinkedIn\'e de yakışır! 💼',
                      'Bu fotoğrafla mimarlık dergilerine kapak olabilirsiniz! 📰',
                      'Büyük mimarlar bile bu fotoğrafı beğenirdi! 🏛️'
                    ];
                    alert(messages[Math.floor(Math.random() * messages.length)]);
                  }, 500);
                }
              }}
            />
          </Button>
          <TextField
            fullWidth
            margin="normal"
            label="İsim"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'background.paper',
              },
              '& .MuiOutlinedInput-input': {
                padding: '14px 16px',
              }
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="E-posta"
            type="email"
            value={email}
            disabled
            InputProps={{
              readOnly: true,
              disabled: true
            }}
            helperText="E-posta adresi değiştirilemez. Yardım için destek ekibiyle iletişime geçin."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'background.paper',
              },
              '& .MuiOutlinedInput-input': {
                padding: '14px 16px',
              },
              '& .MuiInputBase-input': {
                WebkitTextFillColor: theme => theme.palette.text.secondary,
                cursor: 'not-allowed',
              }
            }}
          />
          
          <Box sx={{ 
            mt: 3,
            p: 2.5, 
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{
                color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                fontWeight: 600,
                mb: 1
              }}
            >
              Abonelik Durumu
            </Typography>
            <Typography 
              variant="body2"
              sx={{
                color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                fontWeight: 500
              }}
            >
              Ücretsiz Plan
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 1,
                color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              }}
            >
              Daha fazla özellik ve aylık render kredisi için Temel Paket veya Profesyonel plana yükseltin.
            </Typography>
          </Box>
          
          <Button
            variant="text"
            onClick={() => setShowPasswordFields(!showPasswordFields)}
            sx={{
              mt: 2,
              mb: 1,
              color: theme => theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme => `${theme.palette.primary.main}10`,
              }
            }}
          >
            {showPasswordFields ? 'Şifre Değişimini İptal Et' : 'Şifre Değiştir'}
          </Button>

          {showPasswordFields && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Yeni Şifre"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '14px 16px',
                  }
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Yeni Şifre (Tekrar)"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '14px 16px',
                  }
                }}
              />
            </>
          )}
          
            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                onClick={onClose}
                variant="outlined"
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '8px',
                  fontWeight: 600,
                  borderColor: theme => theme.palette.divider,
                  color: theme => theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme => theme.palette.text.primary,
                    backgroundColor: 'transparent',
                  }
                }}
              >
                İptal
              </Button>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '8px',
                  fontWeight: 600,
                  boxShadow: theme => `0 4px 12px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    boxShadow: theme => `0 6px 16px ${theme.palette.primary.main}60`,
                  }
                }}
              >
                Değişiklikleri Kaydet
              </Button>
            </Box>
          </form>
        )}
        
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Marka Logoları
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Paylaşımlarınızda kullanmak üzere logolarınızı yükleyin. Şeffaf arka planlı PNG formatı önerilir.
            </Typography>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<AddIcon />}
              sx={{
                mb: 3,
                borderRadius: '8px',
                fontWeight: 600,
                borderColor: theme => theme.palette.primary.main,
                color: theme => theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme => theme.palette.primary.dark,
                  backgroundColor: theme => `${theme.palette.primary.main}10`,
                }
              }}
            >
              Yeni Logo Yükle
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp"
                onChange={handleLogoFileChange}
              />
            </Button>
            
            {logoLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : userLogos.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                borderRadius: '8px',
                border: theme => `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="text.secondary">
                  Henüz logo yüklemediniz
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {userLogos.map(logo => (
                  <Grid item xs={12} sm={6} key={logo.id}>
                    <Card sx={{ 
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: theme => theme.palette.mode === 'dark' 
                        ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
                        : '0 4px 12px rgba(0, 0, 0, 0.05)',
                    }}>
                      <Box sx={{ 
                        height: 120, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: theme => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
                        p: 2
                      }}>
                        <CardMedia
                          component="img"
                          image={logo.logo_url}
                          alt={logo.logo_name || "Logo"}
                          sx={{ 
                            maxHeight: '100%', 
                            width: 'auto', 
                            maxWidth: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {logo.logo_name || `Logo ${new Date(logo.created_at).toLocaleDateString()}`}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                        <Tooltip title="Düzenle">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditLogo(logo)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteLogo(logo.id, logo.storage_path)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  fontWeight: 600,
                  borderColor: theme => theme.palette.divider,
                  color: theme => theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme => theme.palette.text.primary,
                    backgroundColor: 'transparent',
                  }
                }}
              >
                Kapat
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Logo Dialog */}
        <Dialog open={logoDialogOpen} onClose={() => {
          setLogoDialogOpen(false);
          setEditingLogoId(null);
          setLogoName('');
        }}>
          <DialogTitle>
            {editingLogoId ? 'Logo Adını Düzenle' : 'Yeni Logo Ekle'}
          </DialogTitle>
          <DialogContent>
            {!editingLogoId && newLogo && (
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                justifyContent: 'center',
                backgroundColor: theme => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
                p: 2,
                borderRadius: '8px'
              }}>
                <img 
                  src={URL.createObjectURL(newLogo)} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 150,
                    objectFit: 'contain'
                  }} 
                />
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Logo Adı"
              fullWidth
              variant="outlined"
              value={logoName}
              onChange={(e) => setLogoName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img 
                      src={editingLogoId ? userLogos.find(l => l.id === editingLogoId)?.logo_url : ''} 
                      alt=""
                      style={{ 
                        width: editingLogoId ? 20 : 0, 
                        height: editingLogoId ? 20 : 0,
                        marginRight: editingLogoId ? 8 : 0
                      }} 
                    />
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setLogoDialogOpen(false);
              setEditingLogoId(null);
              setLogoName('');
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleSaveLogo} 
              variant="contained"
              disabled={logoLoading}
            >
              {logoLoading ? <CircularProgress size={24} /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbars */}
        {notifications.map((notification, index) => (
          <Snackbar
            key={notification.id}
            open={notification.open}
            TransitionComponent={Slide}
            anchorOrigin={{ 
              vertical: 'bottom', 
              horizontal: 'center' 
            }}
            sx={{
              bottom: `${(index * 70) + 24}px !important`, // Stack notifications with spacing
              zIndex: 9999 - index // Ensure proper stacking order
            }}
          >
            <Alert 
              onClose={() => {
                setNotifications(prev => 
                  prev.filter(item => item.id !== notification.id)
                );
              }}
              severity={notification.severity}
              variant="filled"
              elevation={6}
              sx={{ 
                width: '100%',
                borderRadius: '12px',
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem'
                }
              }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </Modal>
  );
};

export default EditProfile;
